import * as colors from '../../00colors.js';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import { demoFunc } from './dees-input-text.demo.js';

import {
  customElement,
  type TemplateResult,
  property,
  state,
  html,
  cssManager,
  css,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-text': DeesInputText;
  }
}

@customElement('dees-input-text')
export class DeesInputText extends DeesInputBase {
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE
  @property({
    type: String,
    reflect: true,
  })
  accessor value: string = '';

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor isPasswordBool = false;

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor showPasswordBool = false;

  @property({
    type: String,
    reflect: true,
  })
  accessor validationState!: 'valid' | 'warn' | 'invalid';

  @property({
    reflect: true,
  })
  accessor validationText: string = '';

  @property({ attribute: false })
  accessor validationFunction!: (value: string) => { valid: boolean; message?: string };

  @property({
    type: Boolean,
    reflect: true,
  })
  accessor vintegrated: boolean = false;

  @property({ attribute: false })
  accessor validConfirmed: boolean = false;

  private validTimeout: ReturnType<typeof setTimeout> | undefined;

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      /* TODO: Migrate hardcoded values to --dees-* CSS variables */
      * {
        box-sizing: border-box;
      }

      :host {
        position: relative;
        z-index: auto;
        font-family: var(--dees-font-family);
      }

      .maincontainer {
        position: relative;
        color: var(--dees-color-text-primary);
      }

      input {
        display: flex;
        height: var(--dees-control-height-lg);
        width: 100%;
        padding: 0 var(--dees-spacing-md);
        font-size: var(--dees-font-control-size);
        line-height: var(--dees-control-height-lg);
        background: var(--dees-color-bg-primary);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        transition: all var(--dees-transition-default) var(--dees-ease-standard);
        outline: none;
        cursor: text;
        font-family: inherit;
        color: var(--dees-color-text-primary);
      }

      input::placeholder {
        color: var(--dees-color-text-muted);
      }

      input:hover:not(:disabled):not(:focus) {
        border-color: var(--dees-color-border-strong);
      }

      input:focus {
        outline: none;
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      input:disabled {
        background: var(--dees-color-badge-default-bg);
        border-color: var(--dees-color-border-default);
        color: var(--dees-color-text-muted);
        cursor: not-allowed;
        opacity: 0.5;
      }

      /* Password toggle button */
      .showPassword {
        position: absolute;
        right: 1px;
        top: calc(var(--dees-control-height-lg) / 2);
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        width: calc(var(--dees-control-height-lg) - 2px);
        height: calc(var(--dees-control-height-lg) - 2px);
        cursor: pointer;
        color: var(--dees-color-text-muted);
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        border-radius: 0 var(--dees-radius-md) var(--dees-radius-md) 0;
      }

      .showPassword:hover {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-text-primary);
      }

      /* Valid checkmark icon */
      .validIcon {
        position: absolute;
        right: 10px;
        top: 20px;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard);
        pointer-events: none;
        color: var(--dees-color-text-success);
      }

      .validIcon.show {
        opacity: 1;
      }

      .validIcon dees-icon {
        font-size: 18px;
      }

      /* Validation styles */
      /* Inline validation: plain accessible-color helper text
         below the field (no filled box). */
      .validationContainer {
        margin-top: var(--dees-spacing-xs);
        padding: 0;
        font-size: var(--dees-font-footnote-size);
        line-height: var(--dees-font-footnote-line);
        font-weight: 400;
        letter-spacing: var(--dees-font-footnote-tracking);
        transition: all var(--dees-transition-default) var(--dees-ease-standard);
        overflow: hidden;
      }

      .validationContainer.invalid {
        background: transparent;
        color: var(--dees-color-text-error);
      }

      .validationContainer.warn {
        background: transparent;
        color: var(--dees-color-text-warning);
      }

      .validationContainer.valid {
        background: transparent;
        color: var(--dees-color-text-success);
      }

      /* Error state for input */
      :host([validationstate="invalid"]) input {
        border-color: var(--dees-color-accent-error);
      }

      :host([validationstate="invalid"]) input:focus {
        border-color: var(--dees-color-accent-error);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--dees-color-accent-error) 25%, transparent);
      }

      /* Warning state for input */
      :host([validationstate="warn"]) input {
        border-color: var(--dees-color-accent-warning);
      }

      :host([validationstate="warn"]) input:focus {
        border-color: var(--dees-color-accent-warning);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--dees-color-accent-warning) 25%, transparent);
      }

      /* Valid state for input */
      :host([validationstate="valid"]) input {
        border-color: var(--dees-color-text-success);
      }

      :host([validationstate="valid"]) input:focus {
        border-color: var(--dees-color-text-success);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--dees-color-text-success) 25%, transparent);
      }

      /* Visually integrated mode: shed chrome to blend into a host component
         (e.g. a dees-table cell in edit mode). */
      :host([vintegrated]) dees-label,
      :host([vintegrated]) .validationContainer {
        display: none;
      }
      :host([vintegrated]) .maincontainer {
        height: var(--dees-control-height-lg);
      }
      :host([vintegrated]) input {
        height: var(--dees-control-height-lg);
        line-height: 24px;
        padding: 0 var(--dees-spacing-lg);
        font-size: var(--dees-font-control-size-sm);
        border: none;
        border-radius: 0;
        background: transparent;
        box-shadow: none;
        transition: none;
      }
      :host([vintegrated]) input:hover:not(:disabled):not(:focus),
      :host([vintegrated]) input:focus {
        border: none;
        box-shadow: none;
        background: transparent;
      }
      :host([vintegrated]) .showPassword {
        display: none;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <style>
        input {
          font-family: ${this.isPasswordBool ? 'var(--dees-font-family-mono)' : 'inherit'};
          letter-spacing: ${this.isPasswordBool ? '0.5px' : 'normal'};
          padding-right: ${this.isPasswordBool ? '48px' : this.validConfirmed ? '40px' : '12px'};
        }
        ${this.validationText && !this.validConfirmed
          ? css`
              .validationContainer {
                height: auto;
                opacity: 1;
                transform: translateY(0);
              }
            `
          : css`
              .validationContainer {
                height: 0;
                padding: 0 !important;
                opacity: 0;
                transform: translateY(-4px);
              }
            `}
      </style>
      <div class="input-wrapper">
        <dees-label .label=${this.label} .infoText=${this.infoText} .required=${this.required}></dees-label>
        <div class="maincontainer">
          <input
            type="${this.isPasswordBool && !this.showPasswordBool ? 'password' : 'text'}"
            .value=${this.value}
            @input="${this.updateValue}"
            .disabled=${this.disabled}
            placeholder="${this.label ? '' : 'Enter text...'}"
          />
          ${this.isPasswordBool
            ? html`
                <div class="showPassword" @click=${this.togglePasswordView}>
                  <dees-icon .icon=${this.showPasswordBool ? 'lucide:Eye' : 'lucide:EyeOff'}></dees-icon>
                </div>
              `
            : html``}
          <div class="validIcon ${this.validConfirmed ? 'show' : ''}">
            <dees-icon .icon=${'lucide:Check'}></dees-icon>
          </div>
          ${this.validationText
            ? html`
                <div class="validationContainer ${this.validationState || 'invalid'}">
                  ${this.validationText}
                </div>
              `
            : html`<div class="validationContainer"></div>`}
          ${this.renderDescription()}
        </div>
      </div>
    `;
  }

  firstUpdated() {
    if (this.validationFunction && this.value) {
      const result = this.validationFunction(this.value);
      this.validationState = result.valid ? 'valid' : 'invalid';
      this.validationText = result.message || '';
      if (result.valid) {
        this.validConfirmed = false;
        this.validTimeout = setTimeout(() => {
          this.validConfirmed = true;
          this.validationState = undefined as any;
        }, 500);
      }
    }
  }

  public async updateValue(eventArg: Event) {
    const target: any = eventArg.target;
    this.value = target.value;
    if (this.validationFunction) {
      const result = this.validationFunction(this.value);
      this.validationState = result.valid ? 'valid' : 'invalid';
      this.validationText = result.message || '';
      if (result.valid) {
        this.validConfirmed = false;
        clearTimeout(this.validTimeout);
        this.validTimeout = setTimeout(() => {
          this.validConfirmed = true;
          this.validationState = undefined as any;
        }, 500);
      } else {
        clearTimeout(this.validTimeout);
        this.validConfirmed = false;
      }
    }
    this.changeSubject.next(this);
  }

  public getContextMenuItems() {
    const input = this.shadowRoot!.querySelector('input')!;
    const hasSelection = input.selectionStart !== input.selectionEnd;
    return [
      {
        name: 'Cut',
        iconName: 'lucide:Scissors',
        shortcut: 'Cmd+X',
        disabled: !hasSelection,
        action: async () => {
          const selected = this.value.substring(input.selectionStart!, input.selectionEnd!);
          await navigator.clipboard.writeText(selected);
          const start = input.selectionStart!;
          this.value = this.value.substring(0, start) + this.value.substring(input.selectionEnd!);
          input.value = this.value;
          input.setSelectionRange(start, start);
          this.changeSubject.next(this);
        },
      },
      {
        name: 'Copy',
        iconName: 'lucide:Copy',
        shortcut: 'Cmd+C',
        disabled: !hasSelection,
        action: async () => {
          const selected = this.value.substring(input.selectionStart!, input.selectionEnd!);
          await navigator.clipboard.writeText(selected);
        },
      },
      {
        name: 'Paste',
        iconName: 'lucide:ClipboardPaste',
        shortcut: 'Cmd+V',
        action: async () => {
          const text = await navigator.clipboard.readText();
          const start = input.selectionStart!;
          const end = input.selectionEnd!;
          this.value = this.value.substring(0, start) + text + this.value.substring(end);
          input.value = this.value;
          input.setSelectionRange(start + text.length, start + text.length);
          this.changeSubject.next(this);
        },
      },
      { divider: true },
      {
        name: 'Select All',
        iconName: 'lucide:TextCursorInput',
        shortcut: 'Cmd+A',
        action: async () => {
          input.select();
        },
      },
    ];
  }

  public getValue(): string {
    return this.value;
  }

  public setValue(value: string): void {
    this.value = value;
  }

  public async togglePasswordView() {
    this.showPasswordBool = !this.showPasswordBool;
  }

  public async focus() {
    const textInput = this.shadowRoot!.querySelector('input');
    textInput!.focus();
  }

  public async blur() {
    const textInput = this.shadowRoot!.querySelector('input');
    textInput!.blur();
  }
}
